import { Intervention } from '@/types';
import { Outlet } from 'react-router-dom';
import { isNil, update, set } from 'lodash/fp';
import { useState, useCallback } from 'react';
import { LocalPathwayScenario } from '@/types';
import { Container, Frame, Content } from '@kinesis/bungle';
import { PathwayScenarioSidebar } from '@/components/scenario-sidebar';
import {
  useGetPathwayScenarioQuery,
  useSavePathwayScenarioMutation,
} from '@/api/pathway-scenarios';
import { useGetInventoryQuery } from '@/api/inventories';
import { usePathwayScenarioId } from '@/hooks/use-scenario-id';
import { useInventoryId } from '@/hooks/use-inventory-id';
import { pathwayScenarioPath } from '@/links';
import { useFlag } from '@/hooks/use-flag';
import { useCopyIntervention } from '@/hooks/use-copy-intervention';

type UpdateLocalPathwayScenario = (
  f: LocalPathwayScenario,
) => LocalPathwayScenario;

const PathwayScenario = () => {
  const inventoryId = useInventoryId();
  const scenarioId = usePathwayScenarioId();
  const { data: inventory } = useGetInventoryQuery(inventoryId);
  const { data: savedPathwayScenario } = useGetPathwayScenarioQuery(scenarioId);
  const enableCopy = useFlag('community.copy');
  const { scenarios, copyIntervention } = useCopyIntervention();

  const [localPathwayScenario, setLocalPathwayScenario] = useState<
    LocalPathwayScenario | undefined
  >(undefined);
  const updatePathwayScenario = useCallback(
    (f: UpdateLocalPathwayScenario) => {
      setLocalPathwayScenario((current) =>
        isNil(current)
          ? f(savedPathwayScenario as LocalPathwayScenario)
          : f(current),
      );
    },
    [savedPathwayScenario, setLocalPathwayScenario],
  );
  const addIntervention = useCallback(
    (i: Intervention) => {
      updatePathwayScenario(update('interventions', (v) => v.concat([i])));
    },
    [updatePathwayScenario],
  );

  const updateIntervention = useCallback(
    (id: number | string, f: (x: Intervention) => Intervention) => {
      updatePathwayScenario(
        update('interventions', (is) => {
          return is.map((i: any) => {
            if (i.id === id) {
              return f(i);
            } else {
              return i;
            }
          });
        }),
      );
    },
    [updatePathwayScenario],
  );
  const removeIntervention = useCallback(
    (k: string | number) => {
      updatePathwayScenario(
        update('interventions', (vs) => vs.filter((v: any) => v.id !== k)),
      );
    },
    [updatePathwayScenario],
  );
  const onRename = useCallback(
    (v: String) => {
      updatePathwayScenario(set('name', v));
    },
    [updatePathwayScenario],
  );

  const onDiscard = useCallback(() => {
    setLocalPathwayScenario(undefined);
  }, [setLocalPathwayScenario]);

  const [savePathwayScenario] = useSavePathwayScenarioMutation();

  const onSave = useCallback(() => {
    setLocalPathwayScenario((current) => {
      savePathwayScenario(current as LocalPathwayScenario);
      return undefined;
    });
  }, [savePathwayScenario]);

  const scenario = localPathwayScenario ?? savedPathwayScenario;
  const isDirty = !isNil(localPathwayScenario);

  return (
    <Container>
      <Frame sizing='fit-content' borderRight>
        <Content padding='none' background='white' height='100%' width={496}>
          {scenario && (
            <PathwayScenarioSidebar
              detailPath={pathwayScenarioPath(inventoryId, scenarioId)}
              location={inventory?.location ?? 'New location'}
              scenario={scenario}
              isDirty={isDirty}
              onRename={onRename}
              onUpdateIntervention={updateIntervention}
              onDiscard={onDiscard}
              onSave={onSave}
              enableCopy={enableCopy}
              scenarios={scenarios}
              onCopyIntervention={copyIntervention}
            />
          )}
        </Content>
      </Frame>
      <Frame sizing='fill-container'>
        {scenario && (
          <Outlet
            context={{
              scenario,
              addIntervention,
              removeIntervention,
              updateIntervention,
            }}
          />
        )}
      </Frame>
    </Container>
  );
};

export { PathwayScenario };
